import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// types
import { GuardProps } from '@/types/bs-types/auth';

// project-imports
import useAuth from '@/hooks/useAuth';
// import { menuAdminItems, menuUserItems } from '@/components/menu-items';
import { adminRoutes } from '../route-main/route-auth/AdminRoutes';
import { paymentRoutes } from '../route-main/route-auth/PaymentRoutes';
import { reseachUserRoutes } from '../route-main/route-auth/UserReseachRoutes';
import ExampleRoutes from '../route-main/route-public/ExampleRoutes';

import { RouteItem } from '@/types/bs-types/menu';
import { getUrlPath } from '@/utils/Path';

// ==============================|| AUTH GUARD ||============================== //
type ITypeDict = {
  dictContainer: any;
  item: RouteItem;
  maintPath: string;
};

const generateChildrenRecursivePaths = ({ dictContainer, item, maintPath }: ITypeDict) => {
  const path = `${maintPath}/${item.path}`;
  if (dictContainer) {
    dictContainer[path] = path;
  }

  if (item && item.children && item.children?.length > 0) {
    item.children?.forEach((it) => {
      return generateChildrenRecursivePaths({ dictContainer, item: it, maintPath: path });
    });
  }
};

const getPathList = (rootPath: string, routes: RouteItem[]) => {
  let userPaths: string[] = [];
  routes?.map((route: RouteItem) => {
    const allDictPaths = {};
    generateChildrenRecursivePaths({ dictContainer: allDictPaths, item: route, maintPath: rootPath });
    const genPaths = Object.keys(allDictPaths);
    userPaths = [...userPaths, ...genPaths];
  });

  return userPaths;
};

// Convert dynamic routes (e.g., /invoice/edit/:id) to regex (e.g., /invoice/edit/\w+)
const isDynamicRouteMatch = (pathname: string, route: string) => {
  const regex = new RegExp(`^${route.replace(/:\w+/g, '\\w+')}$`);
  return regex.test(pathname);
};

// Allow bypass authen path
const pathAllowIncludes = ['/user/payment/pdf']; // /xxx/yyy/zzz

export default function AuthGuard({ children }: GuardProps) {
  const { isLoggedIn, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  let allowAdmins: string[] = [];
  let allowUsers: string[] = [];
  let userAdminRootPath = 'admin';
  let exampleRootPath = 'example';
  let userResearchRootPath = 'latex-purchase';
  let userPaymentRootPath = 'payment';

  useEffect(() => {
    // admin
    // path = > /admin
    allowAdmins = [...getPathList(`/${userAdminRootPath}`, adminRoutes), ...getPathList('', ExampleRoutes.children)];
    // user
    // path = >  /administrative, /research, /payment
    allowUsers = [
      ...getPathList(`/user/${userResearchRootPath}`, reseachUserRoutes),
      ...getPathList('', ExampleRoutes.children),
      ...pathAllowIncludes
    ];

    const isAdminRoute = allowAdmins.some((route) => isDynamicRouteMatch(location.pathname, route));
    const isUserRoute = allowUsers.some((route) => isDynamicRouteMatch(location.pathname, route));

    let defaultPath = `/user/${userResearchRootPath}/my-order`;
    if (user?.activeAccount?.permission === 'CUSTOMER') {
      defaultPath = `/user/${userResearchRootPath}/dashboard`;
    }

    if (!isLoggedIn) {
      navigate('/login', {
        state: {
          from: location.pathname
        },
        replace: true
      });
    } else if (user?.activeAccount?.userRole === 'ADMIN' && isAdminRoute) {
      if (location.pathname === '/') {
        navigate(getUrlPath('/admin/dashboard'), {
          state: {
            from: location.pathname
          },
          replace: true
        });
      }
    } else if (user?.activeAccount?.userRole === 'USER' && isUserRoute) {
      if (location.pathname === '/') {
        navigate(defaultPath, {
          state: {
            from: location.pathname
          },
          replace: true
        });
      }
    } else {
      // console.log(location.pathname + ' : ' + allowAdmins.includes(location.pathname));
      navigate(user?.activeAccount?.userRole === 'USER' ? getUrlPath(defaultPath) : getUrlPath(`/${userAdminRootPath}/dashboard`), {
        state: {
          from: location.pathname
        },
        replace: true
      });
    }
  }, [isLoggedIn, navigate, location]);

  return children;
}
